import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiClientConstant } from '@cureskin/api-client/src/constant/api-client.constant';
import { Experiment } from '@cureskin/api-client/src/server-api';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { FirebaseAndBranchEvent } from '@services/event-logger-service/event-logger.constant';
import { ExperimentService } from '@services/experiment/experiment.service';
import { UserAllocatedDoctorService } from '@services/user-allocated-doctor-service/user-allocated-doctor.service';
import { DoctorProfile } from '@cureskin/api-client-rn';
import { getPlanInfoConfig, planInfoItemId } from '@shared/choose-plan-info/choose-plan-info.constants';

import { AllocatedRegimen } from '../../app-constant-types';
import { AppConfig } from '../../app.config';

@Component({
  selector: 'user-regimen-services',
  templateUrl: './user-regimen-services.html',
  standalone: false,
})
export class UserRegimenServicesComponent implements OnInit {
  regimen: any;
  user: any;
  loading: boolean = true;
  offerTimer: any = {};
  allocatedDoctor: DoctorProfile;
  byPassLocalFetch: boolean = false;
  products: any = [];
  experiments: Array<Experiment>;
  planInfoConfig = getPlanInfoConfig();
  doctorImageUrl: string = '';
  planInfoItemId = planInfoItemId;

  constructor(private eventLogger: EventLoggerService,
    public appConfig: AppConfig,
    private conn: ConnectionService,
    private router: Router,
    private broadcast: BroadcastService,
    private route: ActivatedRoute,
    private readonly experimentService: ExperimentService,
    private readonly userAllocatedDoctorService: UserAllocatedDoctorService) {
    this.doctorImageUrl = this.userAllocatedDoctorService.DEFAULT_DOCTOR_IMAGE_THUMBNAIL;
  }

  async ngOnInit(): Promise<void> {
    const regimenClass = this.route.snapshot.queryParams.class;
    const { regimenId }: any = this.route.snapshot.queryParams;
    this.byPassLocalFetch = true;
    this.user = await this.conn.getActingUser();
    const regimens = await this.conn.fetchRegimens(regimenId, true, this.byPassLocalFetch);
    this.regimen = regimens.find((each: AllocatedRegimen): boolean => each.class === regimenClass);
    await this.setUserExperiments();
    this.regimen.totalMRP = this.regimen?.fixedPriceMRP;
    this.regimen.totalSP = this.regimen?.fixedPrice;
    this.regimen.discount = Math.floor(((this.regimen.totalMRP - this.regimen.totalSP) * 100) / this.regimen.totalMRP);

    if (this.user.get('allocatedDoctor')) {
      this.allocatedDoctor = await this.userAllocatedDoctorService.getAllocatedDoctor();
      this.doctorImageUrl = this.allocatedDoctor?.DoctorImageThumbnail || this.userAllocatedDoctorService.DEFAULT_DOCTOR_IMAGE_THUMBNAIL;
    }

    const uniqueProducts = {};
    [...this.regimen.morning, ...this.regimen.night].forEach((item: any): void => {
      if (item.product.price) uniqueProducts[item.product.objectId] = item;
    });
    this.products = Object.values(uniqueProducts);

    this.loading = false;
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'regimen-services' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  buyRegimen(): void {
    this.eventLogger.trackInParse('BUY_NOW', ApiClientConstant.Event.Type.ORDER);
    this.eventLogger.trackEventForClass(FirebaseAndBranchEvent.BUY_NOW_CLICKED, this.regimen.class);
    this.eventLogger.trackInFirebaseAndBranchForClass(FirebaseAndBranchEvent.BUY_NOW_CLICKED, this.regimen.class);
    const orderState = this.user?.get('orderState');
    this.router.navigate(['/user/checkout'],
      { queryParams: { type: 'REGIMEN', regimenId: this.regimen.regimenId, useCureSkinCash: String(this.experimentService.shouldUseCureSkinCash(orderState)) }, replaceUrl: true });
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  getItemImage(item: any, index: number): string {
    // First item uses regimen cover image
    if (item.id === planInfoItemId.PRODUCTS && this.regimen?.regimenCoverImage) {
      return this.regimen?.regimenCoverImage;
    }
    // Items with 'dedicated_doctor' or 'one_on_one_doctor' id use allocated doctor's image
    if (item.id === planInfoItemId.DEDICATED_DOCTOR || item.id === planInfoItemId.ONE_ON_ONE_DOCTOR) {
      return this.doctorImageUrl || '';
    }
    // Otherwise use the configured image URL
    return item.imageUrl;
  }

  async setUserExperiments(): Promise<void> {
    this.experiments = await this.experimentService.fetchActiveUserExperiment();
    this.planInfoConfig = getPlanInfoConfig([], this.experimentService.videoCallExperiment, this.regimen?.class);
  }
}
